import { useState } from 'react';
import { Copy, Link2Off, Loader2, Plus, QrCode } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { useAuthProviderV2 } from '@/providers/provider-hooks';
import { useCollaboratorLinks } from '../hooks/use-collaborator-links';
import type { CollaboratorLinkDto } from '../api/collaborator-links.api';
import { formatUnixTimestamp } from '@/shared/utils';
import { getErrorMessage } from '@/core/api/response-handler';
import { VITE_COLABORATOR_QR_ACTIVA } from '../utils/colaborator-qr-flag';
import { poateRevocaLinkulDeColaborator } from '../utils/revocare-link-colaborator';

/** Numele fisierului PNG descarcat pentru un link — eticheta lui, fara diacritice/spatii. */
function slugLabel(label: string): string {
    return (
        label
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || 'colaborator'
    );
}

/**
 * Coordonatorul emite linkuri de colaborator (spec §3, D4). Un link = o eticheta (cine il primeste),
 * revocabil, expira singur cu editia. Tokenul nu se afiseaza - doar URL-ul, copiat cu un tap.
 */
export function CollaboratorLinksPage() {
    const { userInfo } = useAuthProviderV2();
    const schoolId = userInfo?.schoolId ? String(userInfo.schoolId) : undefined;
    const { links, create, revoke } = useCollaboratorLinks(schoolId);
    const [label, setLabel] = useState('');
    const [pendingRevoke, setPendingRevoke] = useState<CollaboratorLinkDto | null>(null);

    const copy = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Link copiat. Trimite-l colaboratorului pe WhatsApp sau email.');
        } catch {
            // navigator.clipboard cere context securizat (https sau localhost) - pe un telefon
            // conectat direct la IP-ul de dezvoltare, apelul de mai sus pică. Arătăm linkul ca
            // să poată fi copiat manual, nu doar un mesaj de eroare fără ieșire.
            toast.info(url);
        }
    };

    const downloadQr = async (l: CollaboratorLinkDto) => {
        if (!l.url) return;
        try {
            // Import dinamic: pagina asta e singurul loc care are nevoie de `qrcode` in bundle.
            const { toDataURL } = await import('qrcode');
            const dataUrl = await toDataURL(l.url, { width: 512, margin: 2 });
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `link-${slugLabel(l.label ?? '')}.png`;
            a.click();
        } catch {
            toast.error('Nu am putut genera codul QR.');
        }
    };

    const confirmRevoke = () => {
        if (!pendingRevoke?.id) return;
        revoke.mutate(pendingRevoke.id, { onSettled: () => setPendingRevoke(null) });
    };

    if (!schoolId) {
        // D3 (fix round 1): mesajul de "dupa aprobare" e adevarat doar pentru PROFESOR - un
        // ADMIN/DBEST ajuns aici prin URL direct n-are schoolId in JWT indiferent de aprobare
        // (pagina gestioneaza o singura scoala, fara selector - scope nou, nu aici).
        const message = userInfo?.role === 'PROFESOR'
            ? 'Linkurile de colaborator sunt disponibile după aprobarea școlii.'
            : 'Pagina gestionează linkurile unei singure școli; contul tău nu are o școală asociată.';
        // SE-26: si ramura asta era lipita de marginea ecranului — acelasi `p-4` ca pagina plina,
        // altfel singurul text pe care-l vede un cont fara scoala incepe la 0.
        return <p className="p-4 text-fluid-base">{message}</p>;
    }

    return (
        // `min-w-0`: pagina insasi e item flex intr-un parinte `flex-col`
        // (`ecotic-main-content` din `AuthenticatedLayout`), corect clampat la
        // latimea ecranului. Fara `min-w-0` AICI, radacina paginii ramane la
        // `min-width: auto` implicit — adica la min-content-ul propriilor
        // descendenti — si NU conteaza ca parintele ei e deja 360px: un item
        // flex refuza sa scada sub propriul min-content indiferent cat de mic
        // e containerul lui. Acelasi defect se repeta INTERN, la fiecare nivel
        // `flex-col` cu itemi (vezi `min-w-0` de pe UL si LI mai jos) — un
        // singur nivel fara el e suficient sa lase latimea sa urce pana la
        // radacina documentului.
        //
        // SE-26: `p-4` lipsea, si doar el — restul claselor erau deja identice cu ale vecinei
        // din acelasi tipar (`CereRecipientePage.tsx:114`). Fara el, cardul incepea la 1 px de
        // marginea ecranului la 360, iar pagina „parea lipita" fata de orice alt ecran.
        //
        // Scroller-ul si coloana sunt DOUA elemente (tiparul din `SchoolPointsPage.tsx`): shell-ul
        // taie overflow-ul neconditionat de la `md`, iar cu multe linkuri lista iesea din viewport
        // fara nicio cale de derulare (08.10). `min-w-0` si pe scroller: acum el e itemul flex.
        <div className="min-h-0 w-full min-w-0 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4 min-w-0">
            <Card>
                <CardHeader><CardTitle className="text-fluid-xl">Colaboratori</CardTitle></CardHeader>
                <CardContent className="flex flex-col gap-3">
                    <p className="text-fluid-base">
                        Un colaborator primește un link cu care, fără cont, vede activitățile practice și exercițiile
                        cu clasa (descrierea, filmul, fișa și răspunsurile ei) și trimite dovezile în numele școlii.
                        Ce trimite apare la tine și se punctează pentru școală după aprobare.
                    </p>
                    <form
                        className="flex flex-col gap-2 sm:flex-row"
                        onSubmit={(e) => {
                            e.preventDefault();
                            const trimmed = label.trim();
                            // D4 (fix round 1): golim campul doar la succes - la eroare, userul nu
                            // pierde ce a scris si poate retrimite fara sa retasteze.
                            if (trimmed) create.mutate(trimmed, { onSuccess: () => setLabel('') });
                        }}
                    >
                        <Input
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Pentru cine e linkul (ex. Prof. Ionescu, clasa a VI-a B)"
                            maxLength={120}
                            className="min-h-11 text-base"
                        />
                        <Button type="submit" className="min-h-11 w-full sm:w-auto" disabled={create.isPending || !label.trim()}>
                            <Plus /> Creează link
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {links.isLoading && (
                <p className="flex items-center gap-2 text-dark/60">
                    <Loader2 className="animate-spin" aria-hidden /> Se încarcă linkurile...
                </p>
            )}

            {/* Starea de eroare are ACUM o iesire. Inainte era doar propozitia: userul
                afla ca a picat, dar singura reparatie ramanea reincarcarea paginii —
                care, spre deosebire de `refetch`, pierde ce a scris in formular.
                Acelasi tipar ca la rapoarte (#80): mesaj + „Reîncearcă", pe suprafata,
                fara caseta. Nu refolosesc `ReportErrorState` intreg fiindca acolo
                starea INLOCUIESTE continutul, iar aici formularul de creare ramane
                utilizabil: linkurile nu se pot lista, dar unul nou se poate crea.
                `text-error` in loc de `text-destructive`: tokenul folosit peste tot
                in zona asta. */}
            {links.isError && (
                <div className="flex flex-wrap items-center gap-3">
                    <p className="text-error m-0">
                        {getErrorMessage(links.error, 'Nu am putut încărca linkurile. Încearcă din nou.')}
                    </p>
                    <Button variant="outline" size="sm" onClick={() => links.refetch()} disabled={links.isFetching}>
                        {links.isFetching ? 'Se încearcă...' : 'Reîncearcă'}
                    </Button>
                </div>
            )}

            {/* `min-w-0` pe UL si pe LI: amandoua sunt itemi flex intr-un parinte
                `flex-col` (radacina paginii, respectiv acest UL). Fara el, un
                `min-width` implicit `auto` ia in calcul min-content-ul
                descendentilor — iar `truncate` pe eticheta produce
                `white-space: nowrap`, deci min-content-ul e latimea INTREAGA a
                textului. Fara `min-w-0` la ambele niveluri, latimea aia urca
                prin tot lantul de `flex-col` si largeste pagina intreaga,
                inainte ca `truncate` sa apuce sa taie ceva — masurat la 360px
                cu o eticheta fara spatii: document 662px, in loc de 360. */}
            <ul className="flex flex-col gap-3 min-w-0">
                {links.data?.map((l) => (
                    <li key={l.id} className="min-w-0 rounded-md border border-hairline p-3">
                        <div className="min-w-0">
                            <p className="truncate font-medium">{l.label}</p>
                            <p className="text-sm text-dark/60 tabular-nums">
                                creat {formatUnixTimestamp(l.createdAt)}
                                {l.lastUsedAt ? ` · folosit ${formatUnixTimestamp(l.lastUsedAt)}` : ' · nefolosit'}
                                {!l.active && ' · revocat'}
                            </p>
                        </div>
                        {l.active && (
                            // Actiunile trec pe RAND SEPARAT, sub eticheta: «Copiază link» e acum buton cu
                            // TEXT (decizie owner, 18.09 — o iconita de copiere nu se recunoaste), deci
                            // nu mai incape langa eticheta la 360px fara sa o taie.
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Button
                                    variant="outline"
                                    className="touch-target"
                                    onClick={() => copy(l.url as string)}
                                >
                                    <Copy /> Copiază link
                                </Button>
                                {VITE_COLABORATOR_QR_ACTIVA && (
                                    <Button
                                        variant="outline"
                                        size="icon-lg"
                                        className="touch-target"
                                        aria-label="Descarcă codul QR"
                                        onClick={() => downloadQr(l)}
                                    >
                                        <QrCode />
                                    </Button>
                                )}
                                {/* «Revocă» iese din ecranul PROFESORULUI (decizie owner, 18.09) si
                                    ramane pentru ADMIN/DBEST, care ajung aici prin URL. */}
                                {poateRevocaLinkulDeColaborator(userInfo?.role) && (
                                    <Button
                                        variant="outline"
                                        size="icon-lg"
                                        className="touch-target"
                                        aria-label="Revocă linkul"
                                        onClick={() => setPendingRevoke(l)}
                                        disabled={revoke.isPending}
                                    >
                                        <Link2Off />
                                    </Button>
                                )}
                            </div>
                        )}
                    </li>
                ))}
                {links.data?.length === 0 && <li className="text-dark/60">Niciun link încă.</li>}
            </ul>

            {/* P1-16: descrierea suna la prezent, ca o actiune automata a sistemului
                ("colaboratorul primeste un link nou") -- nimic nu se trimite; sensul
                real e "daca mai vrei sa-i dai acces, creezi TU alt link". Si butoanele
                "Anulare"/"Revoca" sunt aproape sinonime in romana pentru cineva grabit
                -- butonul de iesire numeste acum iesirea, nu doar "Anulare". */}
            <ConfirmationDialog
                isOpen={pendingRevoke !== null}
                onClose={() => setPendingRevoke(null)}
                onConfirm={confirmRevoke}
                title="Revocă linkul"
                description={`Linkul „${pendingRevoke?.label ?? ''}” nu va mai putea fi folosit. Dacă vrei să mai dai acces, poți crea un link nou.`}
                confirmText="Revocă"
                cancelText="Renunță"
                variant="destructive"
                loading={revoke.isPending}
            />
        </div>
        </div>
    );
}
